import { createBrowserRouter, createRoutesFromElements, Navigate, Route } from "react-router-dom";
import PrivateRoute from "./components/auth/routeGuards/PrivateRoute";
import FileViewer from "./components/common/FileViewer";
import Layout from "./components/layout/Layout";
import { PageTitleKeys, RoutePaths } from "./config/routeConstants";
import PrivateLayout from './layouts/PrivateLayout';
import PublicLayout from './layouts/PublicLayout';
import AdminDataSourceList from "./pages/admin/AdminDataSourceList";
import AppInfo from "./pages/admin/AppInfo";
import Logs from "./pages/admin/Logs";
import Configuration from "./pages/admin/Configuration";
import CreateUser from "./pages/admin/CreateUser";
import DataSourceDocumentList from "./pages/admin/DataSourceDocumentList";
import DataSourceUpdateScheduler from "./pages/admin/DataSourceUpdateScheduler";
import GroupDetails from "./pages/admin/GroupDetails";
import GroupList from "./pages/admin/GroupList";
import Prompts from "./pages/admin/Prompts";
import ReportedIssuesList from "./pages/admin/ReportedIssuesList";
import UserEdit from "./pages/admin/UserEdit";
import UserList from "./pages/admin/UserList";
import AccountActivation from "./pages/auth/AccountActivation";
import ChangePassword from "./pages/auth/ChangePassword";
import CheckYourMailbox from "./pages/auth/CheckYourMailbox";
import EmailConfirmed from "./pages/auth/EmailConfirmed";
import FirstPassword from "./pages/auth/FirstPassword";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register/Register";
import RequestPasswordReset from "./pages/auth/RequestPasswordReset";
import ThankYouForRegistration from "./pages/auth/ThankYouForRegistration";
import Manual from "./pages/manual/Manual";
import UserSettings from "./pages/user-settings/UserSettings";
import ChatWorkspace from "./pages/workspace/ChatWorkspace";
import PaymentFailure from "./pages/workspace/Payments/PaymentFailure";
import Payments from "./pages/workspace/Payments/Payments";
import PaymentSuccess from "./pages/workspace/Payments/PaymentSuccess";
import UserDataSourceList from "./pages/workspace/UserDataSourceList";
import FirstInstanceGuard from './components/auth/routeGuards/FirstInstanceGuard';
import RegisterAdmin from './pages/auth/Register/RegisterAdmin';

export const router = createBrowserRouter(
    createRoutesFromElements(
        <Route path={RoutePaths.Root}>
            <Route path={RoutePaths.RegisterAdmin} element={<RegisterAdmin/>} handle={{titleKey: PageTitleKeys.Register}}/>

            <Route element={<FirstInstanceGuard/>}>
                <Route element={<PublicLayout/>}>
                    <Route path={RoutePaths.Login} element={<Login/>} handle={{titleKey: PageTitleKeys.Login}}/>
                    <Route path={RoutePaths.Register} element={<Register/>} handle={{titleKey: PageTitleKeys.Register}}/>
                    <Route path={RoutePaths.ThankYouForRegistration} element={<ThankYouForRegistration/>}
                           handle={{titleKey: PageTitleKeys.ThankYouForRegistration}}/>
                    <Route path={RoutePaths.EmailConfirmed} element={<EmailConfirmed/>}
                           handle={{titleKey: PageTitleKeys.EmailConfirmed}}/>
                    <Route path={RoutePaths.RequestPasswordReset} element={<RequestPasswordReset/>}
                           handle={{titleKey: PageTitleKeys.RequestPasswordReset}}/>
                    <Route path={RoutePaths.ChangePassword} element={<ChangePassword/>}
                           handle={{titleKey: PageTitleKeys.ChangePassword}}/>
                    <Route path={RoutePaths.FirstPasswordSetup} element={<FirstPassword/>}
                           handle={{titleKey: PageTitleKeys.FirstPasswordSetup}}/>
                    <Route path={RoutePaths.CheckMailbox} element={<CheckYourMailbox/>}
                           handle={{titleKey: PageTitleKeys.CheckMailbox}}/>
                    <Route path={RoutePaths.AccountActivation} element={<AccountActivation/>}
                           handle={{titleKey: PageTitleKeys.AccountActivation}}/>
                    <Route path={RoutePaths.Manual} element={<Manual/>} handle={{titleKey: PageTitleKeys.Manual}}/>
                </Route>
            </Route>

            <Route element={<PrivateLayout/>}>
                <Route element={<PrivateRoute/>}>
                    <Route path={RoutePaths.Root} element={<Layout/>} handle={{titleKey: PageTitleKeys.Home}}>
                        <Route path={RoutePaths.Admin}>
                            <Route path={RoutePaths.AdminGroupList} Component={GroupList}
                                   handle={{titleKey: PageTitleKeys.AdminGroupList}}/>
                            <Route path={RoutePaths.AdminGroupDetails} Component={GroupDetails}
                                   handle={{titleKey: PageTitleKeys.AdminGroupDetails}}/>
                            <Route path={RoutePaths.AdminUserCreate} element={<CreateUser/>}
                                   handle={{titleKey: PageTitleKeys.AdminUserCreate}}/>
                            <Route path={RoutePaths.AdminUserList} element={<UserList/>}
                                   handle={{titleKey: PageTitleKeys.AdminUserList}}/>
                            <Route path={RoutePaths.AdminUserEdit} element={<UserEdit/>}
                                   handle={{titleKey: PageTitleKeys.AdminUserEdit}}/>
                            <Route path={RoutePaths.AdminDataSourcesList} element={<AdminDataSourceList/>}
                                   handle={{titleKey: PageTitleKeys.AdminDataSourcesList}}/>
                            <Route path={RoutePaths.AdminScheduler} Component={DataSourceUpdateScheduler}
                                   handle={{titleKey: PageTitleKeys.AdminScheduler}}/>
                            <Route path={RoutePaths.AdminReportedIssues} element={<ReportedIssuesList/>}
                                   handle={{titleKey: PageTitleKeys.AdminReportedIssues}}/>
                            <Route path={RoutePaths.AdminPrompts} element={<Prompts/>}
                                   handle={{titleKey: PageTitleKeys.AdminPrompts}}/>
                            <Route path={RoutePaths.AdminConfiguration} element={<Configuration/>}
                                   handle={{titleKey: PageTitleKeys.AdminConfiguration}}/>
                            <Route path={RoutePaths.AdminAppInfo} element={<AppInfo/>}
                                   handle={{titleKey: PageTitleKeys.AdminAppInfo}}/>
                            <Route path={RoutePaths.AdminLogs} element={<Logs/>}
                                   handle={{titleKey: PageTitleKeys.AdminLogs}}/>
                        </Route>
                        <Route path={RoutePaths.Chat} element={<ChatWorkspace/>} handle={{titleKey: PageTitleKeys.Chat}}/>
                        <Route path={RoutePaths.ChatWithId} element={<ChatWorkspace/>}
                               handle={{titleKey: PageTitleKeys.ChatWithId}}/>
                        <Route path={RoutePaths.UserDataSourcesList} element={<UserDataSourceList/>}
                               handle={{titleKey: PageTitleKeys.UserDataSourcesList}}/>
                        <Route path={RoutePaths.DataSourceDocuments} element={<DataSourceDocumentList/>}
                               handle={{titleKey: PageTitleKeys.DataSourceDocuments}}/>
                        <Route path={RoutePaths.DocumentView} Component={FileViewer}
                               handle={{titleKey: PageTitleKeys.DocumentView}}/>
                        <Route path={RoutePaths.Payments} element={<Payments/>}
                               handle={{titleKey: PageTitleKeys.Payments}}/>
                        <Route path={RoutePaths.PaymentSuccess} element={<PaymentSuccess/>}
                               handle={{titleKey: PageTitleKeys.PaymentSuccess}}/>
                        <Route path={RoutePaths.PaymentFailure} element={<PaymentFailure/>}
                               handle={{titleKey: PageTitleKeys.PaymentFailure}}/>
                        <Route path={RoutePaths.UserProfile} element={<UserSettings/>}
                               handle={{titleKey: PageTitleKeys.UserProfile}}/>
                        <Route index element={<Navigate to={RoutePaths.Chat} replace/>}
                               handle={{titleKey: PageTitleKeys.Home}}/>
                    </Route>
                </Route>
            </Route>

            <Route path={RoutePaths.CatchAll} element={<Login/>} handle={{titleKey: PageTitleKeys.Login}}/>
        </Route>
    ), {
        future: {
            v7_relativeSplatPath: true,
            v7_normalizeFormMethod: true,
            v7_partialHydration: true,
            v7_fetcherPersist: true,
            v7_skipActionErrorRevalidation: true,
        },
    }
);
